import emailjs from 'emailjs-com';
import { toast } from 'sonner';

export interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export type FormField = 'name' | 'email' | 'subject' | 'message';

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const submitContactForm = async (
  contactFormData: ContactFormData,
  sessionId: string,
  onSuccess: (message: Message) => void,
  onError: (message: Message) => void
): Promise<void> => {
  try {
    const templateParams = {
      from_name: contactFormData.name,
      from_email: contactFormData.email,
      to_email: 'rafa.ferreira@gmail.com',
      subject: contactFormData.subject || 'Contato via Chat',
      message: contactFormData.message
    };

    await emailjs.send(
      'service_spr1ggv',
      'template_19yj66g',
      templateParams,
      '_m_MDC3y8ocLk7Ytf'
    );
    
    const botMessage: Message = {
      id: Date.now().toString(),
      text: 'Mensagem enviada com sucesso! Obrigado pelo seu contato. Como posso ajudar você com mais alguma coisa?',
      sender: 'bot',
      timestamp: new Date()
    };
    
    onSuccess(botMessage);
    toast.success("Mensagem enviada com sucesso!");
    
  } catch (error) {
    console.error('EmailJS Error:', error);
    
    const botMessage: Message = {
      id: Date.now().toString(),
      text: 'Desculpe, ocorreu um erro ao enviar sua mensagem. Por favor, tente novamente mais tarde ou entre em contato pelo telefone (19) 98765-4321.',
      sender: 'bot',
      timestamp: new Date()
    };
    
    onError(botMessage);
    toast.error("Erro ao enviar mensagem. Tente novamente.");
  }
};

// Track if we've already shown the help message to avoid repetition
let hasShownHelpMessage = false;

export const sendChatMessage = async (
  message: string,
  sessionId: string,
  apiUrl: string,
  moreDetails: boolean = false,
  onSuccess: (message: Message) => void,
  onError: (message: Message) => void
): Promise<void> => {
  try {
    const response = await fetch(`${apiUrl}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId
      },
      body: JSON.stringify({ 
        message: message,
        moreDetails: moreDetails 
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Erro na API: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Process the response to remove any introduction
    let responseText = data.response || '';
    
    // Function to check if the response starts with a greeting
    const startsWithGreeting = (text: string): boolean => {
      const greetingWords = ['olá', 'oi', 'olá!', 'oi!', 'prazer', 'bom dia', 'boa tarde', 'boa noite'];
      const lowerText = text.toLowerCase().trim();
      
      // Check if the text starts with any greeting word
      return greetingWords.some(word => lowerText.startsWith(word));
    };
    
    // Remove common introduction patterns
    const introPatterns = [
      // Original patterns
      /^Olá!?\s+(?:Eu\s+)?sou\s+o\s+Assistente\s+Moa[,.].*?(?=\n\n|\.\s+)/i,
      /^Olá!?\s+(?:Eu\s+)?sou\s+o\s+\*?Assistente\s+Moa\*?[,.].*?(?=\n\n|\.\s+)/i,
      /^(?:Olá|Oi)!?\s+(?:Eu\s+)?sou\s+(?:o\s+)?(?:seu)?\s*assistente.*?(?=\n\n|\.\s+)/i,
      /^(?:Olá|Oi)[,.]?\s+(?:como\s+posso\s+ajudar|em\s+que\s+posso\s+ajudar).*?(?=\n\n|\.\s+)/i,
      
      // Additional patterns
      /^Olá,\s+sou\s+o\s+Assistente\s+Moa,.*?(?=\n\n|\.\s+)/i,
      /^Olá,\s+sou\s+o\s+Assistente\s+Moa[,!]?\s+(?:da\s+Perícia\s+Técnica)?[,!]?\s+(?:prazer\s+em\s+(?:ajudá-lo|conhecê-lo|conversar\s+com\s+você)).*?(?=\n\n|\.\s+)/i,
      /^Olá,\s+sou\s+o\s+Assistente\s+Moa[,!]?\s+(?:prazer\s+em\s+(?:ajudá-lo|conhecê-lo|conversar\s+com\s+você)).*?(?=\n\n|\.\s+)/i,
      /^(?:Olá|Oi)[,!]?\s+(?:sou|me\s+chamo)\s+(?:o\s+)?Assistente\s+Moa.*?(?=\n\n|\.\s+)/i,
      
      // Catch-all pattern for any greeting with "Assistente Moa" in the first sentence
      /^(?:Olá|Oi|Olá!)[,!]?.*?Assistente\s+Moa.*?(?=\n\n|\.\s+)/i,
      
      // Super aggressive pattern - remove first sentence if it contains "Assistente Moa" and starts with a greeting
      /^[^.!?]*(?:Assistente\s+Moa)[^.!?]*[.!?]/i
    ];
    
    // First try the specific patterns
    for (const pattern of introPatterns) {
      responseText = responseText.replace(pattern, '');
    }
    
    // If the response still starts with a greeting and mentions "Moa" in the first sentence,
    // try to remove the first sentence entirely
    if (startsWithGreeting(responseText) && responseText.toLowerCase().split(/[.!?]/)[0].includes('moa')) {
      responseText = responseText.replace(/^[^.!?]*[.!?]\s*/, '');
    }
    
    // Trim any leading whitespace or punctuation after removing the introduction
    responseText = responseText.replace(/^[\s,.;:!?]+/, '');
    
    // Ensure the first letter is capitalized if we removed the introduction
    if (responseText.length > 0) {
      responseText = responseText.charAt(0).toUpperCase() + responseText.slice(1);
    }
    
    // Only add the help suffix if we haven't shown it before
    let suffix = "";
    if (!hasShownHelpMessage && !moreDetails) {
      suffix = "\n\nPosso tentar explicar melhor se precisar, basta pedir /mais. Se preferir falar com um humano, clique no ícone de envelope acima para enviar uma mensagem!";
      hasShownHelpMessage = true;
    } else if (moreDetails) {
      suffix = "\n\nEspero que isso ajude! Caso precise de mais assistência, recomendo enviar uma mensagem para nossa equipe clicando no ícone de envelope acima.";
    }
    
    const botResponse: Message = {
      id: (Date.now() + 1).toString(),
      text: responseText + suffix,
      sender: 'bot',
      timestamp: new Date()
    };
    
    onSuccess(botResponse);
    
  } catch (err) {
    console.error('Erro ao enviar mensagem:', err);
    
    const errorMessage: Message = {
      id: (Date.now() + 1).toString(),
      text: 'Desculpe, estou com dificuldades para responder agora. Por favor, tente novamente mais tarde ou clique no ícone de envelope acima para enviar uma mensagem diretamente para nossa equipe. Eles poderão te ajudar!',
      sender: 'bot',
      timestamp: new Date()
    };
    
    onError(errorMessage);
  }
};
